import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { StorageAvatar } from '../components/common/StorageAvatar';
import { useAccount } from '../contexts/AccountContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { firebaseConfig } from '../services/firebase';
import { downloadPartnerDocument, getPartner, registerPartner } from '../services/partnerService';
import storageService from '../services/storageService';
import { getUsers } from '../services/userService';
import { COUNTRIES, getRegionsForCountry } from '../shared/constants/locations';
import type { User } from '../types/auth';
import type { Partner } from '../types/partner';

const PartnerRegistrationPage: React.FC = () => {
  const theme = useTheme();
  const { account } = useAccount();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState(account?.clientName || '');
  const [capabilitiesSummary, setCapabilitiesSummary] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [contactEmail, setContactEmail] = useState(account?.email || '');
  const [contactPhone, setContactPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('Australia');
  const [region, setRegion] = useState('');
  const [designatedReceiverId, setDesignatedReceiverId] = useState<string>('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [existingCapabilityUrl, setExistingCapabilityUrl] = useState<string | null>(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const onFileDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0 && acceptedFiles[0]) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const onLogoDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0 && acceptedFiles[0]) {
        const selectedFile = acceptedFiles[0];
        if (selectedFile.size > 2 * 1024 * 1024) {
          showSnackbar('Logo file size must be less than 2MB.', 'error');
          return;
        }
        setLogoFile(selectedFile);
      }
    },
    [showSnackbar]
  );

  const {
    getRootProps: getFileRootProps,
    getInputProps: getFileInputProps,
    isDragActive: isFileDragActive,
  } = useDropzone({
    onDrop: onFileDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
  });

  const {
    getRootProps: getLogoRootProps,
    getInputProps: getLogoInputProps,
    isDragActive: isLogoDragActive,
  } = useDropzone({
    onDrop: onLogoDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'],
    },
    maxFiles: 1,
  });

  // Check if client has matchmaking access
  useEffect(() => {
    const fetchExistingProfile = async () => {
      if (!account?.clientId) {
        setFetching(false);
        return;
      }
      try {
        const usersResponse = await getUsers();
        if (usersResponse.success && usersResponse.users) {
          setAvailableUsers(usersResponse.users);
        }

        const data = (await getPartner(account.clientId)) as unknown as Partner;
        if (data) {
          setCompanyName(data.companyName || account.clientName || '');
          setCapabilitiesSummary(data.capabilitiesSummary || '');
          setAdditionalInfo(data.additionalInfo || '');
          setContactEmail(data.contactEmail || account.email || '');
          setContactPhone(data.contactPhone || '');
          setWebsite(data.website || '');
          setCountry(data.country || 'Australia');
          setRegion(data.region || '');
          setDesignatedReceiverId(data.designatedReceiverId || '');
          if (data.capabilityStatementUrl) {
            setExistingCapabilityUrl(data.capabilityStatementUrl);
          }
          if (data.logoUrl) {
            setExistingLogoUrl(data.logoUrl);
          }
        }
      } catch (_err) {
        // Silently handle error
      } finally {
        setFetching(false);
      }
    };

    fetchExistingProfile();
  }, [account?.clientId, account?.clientName, account?.email]);

  if (account && !account.hasMatchmakingAccess) {
    return (
      <PageContainer title="Partner Profile">
        <Alert severity="error" sx={{ mt: 2 }}>
          Your organization does not have access to the Subcontractor Matchmaking service. Please
          contact your administrator.
        </Alert>
      </PageContainer>
    );
  }

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName || !contactEmail || !capabilitiesSummary) {
      showSnackbar(
        'Please fill in all required fields, including your Capability Statement text.',
        'error'
      );
      return;
    }

    if (!file && !existingCapabilityUrl) {
      showSnackbar('You must upload a capability statement.', 'error');
      return;
    }

    if (!account?.clientId) {
      showSnackbar('You must belong to a client to register a partner profile.', 'error');
      return;
    }

    setLoading(true);

    try {
      let finalCapabilityUrl = existingCapabilityUrl || '';

      // Upload the file to Firebase Storage if a new one was provided
      if (file) {
        const filePath = `clients/${account.clientId}/partner_profile/${file.name}`;
        const uploadResult = await storageService.uploadFile(filePath, file);

        if (!uploadResult.success) {
          throw new Error('File upload failed.');
        }
        finalCapabilityUrl = `gs://${firebaseConfig.storageBucket}/${filePath}`;
      }

      // Upload the logo to Firebase Storage if provided
      let logoUrlStr = '';
      if (logoFile) {
        const logoPath = `public/partner_logos/${account.clientId}_${logoFile.name}`;
        const logoUploadResult = await storageService.uploadFile(logoPath, logoFile);
        if (logoUploadResult.success) {
          logoUrlStr = logoUploadResult.downloadURL;
        } else {
          showSnackbar('Warning: Logo upload failed, continuing registration.', 'warning');
        }
      }

      // Format website URL to ensure it starts with http/https
      let formattedWebsite = website.trim();
      if (formattedWebsite && !/^https?:\/\//i.test(formattedWebsite)) {
        formattedWebsite = `https://${formattedWebsite}`;
      }

      // Register the profile via Cloud Function
      const payload: Parameters<typeof registerPartner>[0] = {
        companyName,
        capabilitiesSummary,
        additionalInfo,
        contactEmail,
        capabilityStatementUrl: finalCapabilityUrl,
      };

      if (contactPhone) {
        payload.contactPhone = contactPhone;
      }
      if (formattedWebsite) {
        payload.website = formattedWebsite;
      }
      if (country) {
        payload.country = country;
      }
      if (region) {
        payload.region = region;
      }
      if (logoUrlStr) {
        payload.logoUrl = logoUrlStr;
      }
      if (designatedReceiverId) {
        payload.designatedReceiverId = designatedReceiverId;
      }

      const result = await registerPartner(payload);

      if (result.success) {
        showSnackbar('Partner profile registered successfully.', 'success');
        navigate('/dashboard');
      } else {
        throw new Error(result.message || 'Registration failed.');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred.';
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer title="Partner Profile">
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Register as a Subcontractor / JV Partner
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Join our global database to be discovered by other contractors during their bid capability
          analysis. Provide your details and upload a Capability Statement to ensure the AI can
          match your skills accurately.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
            Instructions & Template
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2, fontWeight: 500 }}>
            Please ensure your capability statement covers your core technical abilities, past
            project experience, and risk management practices. We highly recommend branding and
            customizing the provided template to make it more visually appealing and aligned with
            your company's identity.
          </Typography>
          <Button
            href="/default_capability_statement.docx"
            target="_blank"
            rel="noopener"
            variant="contained"
            size="large"
            startIcon={<DownloadIcon />}
            sx={{
              mt: 1,
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
              '&:hover': {
                backgroundColor: theme.palette.bidlogiq?.brick || 'primary.dark',
                filter: 'brightness(0.9)',
              },
            }}
          >
            Download Capability Statement Template
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    label="Company Name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    label="Contact Email"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Contact Phone"
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Website URL"
                    type="text"
                    placeholder="e.g. www.example.com"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    fullWidth
                    disabled
                    label="Country"
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      setRegion('');
                    }}
                    FormHelperTextProps={{ sx: { fontSize: '0.875rem' } }}
                    helperText="Currently limited to Australia."
                  >
                    <MenuItem value="">
                      <em>Select Country</em>
                    </MenuItem>
                    {COUNTRIES.map((c) => (
                      <MenuItem key={c.code} value={c.name}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={getRegionsForCountry(country)}
                    disabled={!country}
                    value={region || null}
                    onChange={(_, newValue) => setRegion(newValue || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Region / State"
                        FormHelperTextProps={{ sx: { fontSize: '0.875rem' } }}
                        helperText="Select your primary operating region."
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={availableUsers}
                    getOptionLabel={(option) =>
                      `${option.displayName || option.email || 'Unknown'} (${option.email})`
                    }
                    value={availableUsers.find((u) => u.uid === designatedReceiverId) || null}
                    onChange={(_, newValue) => setDesignatedReceiverId(newValue?.uid || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Designated Receiver (Optional)"
                        FormHelperTextProps={{ sx: { fontSize: '0.875rem' } }}
                        helperText="Select the user in your organization who should receive partnership invitations. If left blank, invitations will go to the profile creator."
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom fontWeight="medium" color="primary">
                    Capability Statement Text (Required)
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Please copy and paste a clear capability statement here (max 2,000 characters,
                    approx 300 words).
                    <strong>
                      {' '}
                      This text will be displayed directly on your profile for other users to read.
                    </strong>
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={8}
                    inputProps={{ maxLength: 2000 }}
                    placeholder="Enter your capability statement here..."
                    value={capabilitiesSummary}
                    onChange={(e) => setCapabilitiesSummary(e.target.value)}
                    helperText={`${capabilitiesSummary.length} / 2000 characters`}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                    Capability Statement Document (Required)
                  </Typography>
                  <Typography variant="body1" color="text.primary" sx={{ mb: 2, fontWeight: 500 }}>
                    Please upload a comprehensive capability statement (DOCX or PDF).
                    <strong>
                      {' '}
                      This document is used by our AI to match you against tender requirements
                      behind the scenes.
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    • <strong>More details = more likelihood of being matched.</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    • Make sure it is clear, representative, and partnership-focused.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    •{' '}
                    <em>
                      Warning: Do not invent capabilities. Dishonesty may result in permanent
                      exclusion from the partner matchmaking service.
                    </em>
                  </Typography>

                  {existingCapabilityUrl && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: 'orange', mb: 1 }}>
                        You already have a capability statement uploaded. Uploading a new file will
                        replace your current statement.
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<DownloadIcon />}
                        onClick={async () => {
                          if (!account?.clientId) {
                            return;
                          }
                          try {
                            // Assumes you have imported downloadPartnerDocument at the top of the file
                            const blob = await downloadPartnerDocument(account.clientId);
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            let extension = 'docx';
                            const parts = existingCapabilityUrl.split('.');
                            if (parts.length > 1) {
                              const lastPart = parts.pop();
                              if (lastPart && lastPart.length < 6 && !lastPart.includes('/')) {
                                extension = lastPart;
                              }
                            }
                            a.download = `capability_statement.${extension}`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(url);
                          } catch (_err) {
                            showSnackbar('Failed to download capability statement.', 'error');
                          }
                        }}
                        sx={{
                          fontWeight: 'bold',
                          color: 'white',
                          backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
                          '&:hover': {
                            backgroundColor: theme.palette.bidlogiq?.brick || 'primary.dark',
                            filter: 'brightness(0.9)',
                          },
                        }}
                      >
                        Download Current Statement
                      </Button>
                    </Box>
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      {...getFileRootProps()}
                      sx={{
                        py: 6,
                        px: 2,
                        border: '2px dashed',
                        borderColor: isFileDragActive ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        bgcolor: isFileDragActive ? 'action.hover' : 'background.paper',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <input {...getFileInputProps()} />
                      <CloudUploadIcon
                        color={isFileDragActive ? 'primary' : 'action'}
                        sx={{ fontSize: 40, mb: 1 }}
                      />
                      <Typography
                        variant="body1"
                        color={file ? 'text.primary' : 'text.secondary'}
                        fontWeight={file ? 'bold' : 'normal'}
                      >
                        {file
                          ? file.name
                          : existingCapabilityUrl
                            ? 'Drag & drop a new file here, or click to replace'
                            : 'Drag & drop your Capability Statement here, or click to select'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" gutterBottom fontWeight="medium">
                    Company Logo
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Upload your company logo. For best results, use a square image (e.g. 500x500px),
                    max file size 2MB, with a light or transparent background.
                  </Typography>
                  {existingLogoUrl && !logoFile && (
                    <Box sx={{ mt: 2 }}>
                      <StorageAvatar
                        src={existingLogoUrl}
                        variant="square"
                        imgProps={{ sx: { objectFit: 'contain' } }}
                        sx={{
                          width: 100,
                          height: 100,
                          bgcolor: 'transparent',
                        }}
                      >
                        {companyName.charAt(0)}
                      </StorageAvatar>
                    </Box>
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      {...getLogoRootProps()}
                      sx={{
                        flexGrow: 1,
                        py: 4,
                        px: 2,
                        border: '2px dashed',
                        borderColor: isLogoDragActive ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        bgcolor: isLogoDragActive ? 'action.hover' : 'background.paper',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <input {...getLogoInputProps()} />
                      <CloudUploadIcon
                        color={isLogoDragActive ? 'primary' : 'action'}
                        sx={{ fontSize: 32, mb: 1 }}
                      />
                      <Typography
                        variant="body1"
                        color={logoFile ? 'text.primary' : 'text.secondary'}
                        fontWeight={logoFile ? 'bold' : 'normal'}
                      >
                        {logoFile
                          ? logoFile.name
                          : 'Drag & drop your Logo Image here, or click to select'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom fontWeight="medium">
                    Anything else to add? (optional)
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Include brief statements about your target focus, minimum contract cost, or
                    specific exclusions.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="e.g. We only take on projects > $1M. We specialize in tier 1..."
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    disabled={loading || (!file && !existingCapabilityUrl)}
                    sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : existingCapabilityUrl ? (
                      'Update Partner Profile'
                    ) : (
                      'Register Partner Profile'
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default PartnerRegistrationPage;
